import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useGetComplianceBulletinContentMutation } from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
};

/**
 * Full-page view of one Compliance Quick Hitter issue, at
 * /compliance/quick-hitter/view/:bulletinId.
 *
 * This replaced a modal. Opening in a tab means the issue gets a real URL that
 * can be bookmarked, shared in Teams, and printed, and it gets the whole window
 * height rather than 90vh of a dialog — these documents are a full page of
 * text, and the modal made people scroll a small box.
 *
 * The bulletin is still rendered in a SANDBOXED IFRAME rather than injected into
 * the page, for the same two reasons the modal did it:
 *   1. Compliance author these as complete HTML documents with their own CSS.
 *      Injecting that inline would leak their styles into the hub and let the
 *      hub's styles break their layout.
 *   2. It's uploaded HTML. Even from a trusted internal author, putting it
 *      through dangerouslySetInnerHTML would be an XSS vector. Having its own
 *      tab does not change that — the tab is still on the hub's origin.
 *
 * `sandbox="allow-popups allow-popups-to-escape-sandbox"` deliberately omits
 * allow-scripts and allow-same-origin: the bulletins are static documents, and
 * the popup permissions are only so their external links (NASAA, FINRA) still
 * open in a new tab.
 */
export default function ComplianceQuickHitterView() {
  const { bulletinId } = useParams();
  const [fetchContent] = useGetComplianceBulletinContentMutation();
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!bulletinId) return undefined;
    setContent(null);
    setError(null);
    fetchContent({ id: bulletinId })
      .unwrap()
      .then((res) => { if (!cancelled) setContent(res); })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.data?.detail || 'Could not load this bulletin.');
        }
      });
    return () => { cancelled = true; };
  }, [bulletinId, fetchContent]);

  // The document is the point of this page, so give it the window rather than
  // the usual card-in-a-column layout. 9rem covers the hub's top header plus
  // this page's own title bar and padding.
  const frameHeight = 'calc(100vh - 9rem)';
  const heading = content?.title || 'Compliance Quick Hitter';

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{
        background: 'white', borderRadius: 12, overflow: 'hidden',
        boxShadow: '0 2px 12px rgba(10,41,71,0.1)',
      }}>
        <div style={{
          background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`,
          padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
        }}>
          <i className="bi bi-clipboard2-check-fill" style={{ color: brand.goldenrod }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h5 style={{
              color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase',
              letterSpacing: '1px', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {heading}
            </h5>
            {content?.period_label && (
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem' }}>
                {content.period_label}
              </div>
            )}
          </div>
          {/* PDFs come back as a presigned URL, so they can be handed straight to
              the browser's own viewer — which is also the only way to print one.
              HTML issues have no equivalent (srcDoc has no URL), so no link. */}
          {content?.kind === 'pdf' && (
            <a
              href={content.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'white', fontSize: '0.78rem', textDecoration: 'none' }}
            >
              <i className="bi bi-box-arrow-up-right" style={{ marginRight: '0.35rem' }} />
              Open the PDF
            </a>
          )}
          <Link
            to="/dashboard"
            style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem', textDecoration: 'none' }}
          >
            <i className="bi bi-house-door-fill" style={{ marginRight: '0.35rem' }} />
            Hub
          </Link>
        </div>

        <div style={{ height: frameHeight, background: '#f4f4f4' }}>
          {error && (
            <div style={{ padding: '1.5rem', color: '#b00020' }}>
              <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: '0.5rem' }} />
              {error}
            </div>
          )}
          {!error && !content && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
            }}>
              <div className="spinner-border" style={{ color: brand.tealBlue }} />
            </div>
          )}
          {content?.kind === 'html' && (
            <iframe
              title={heading}
              srcDoc={content.html}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
              style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
            />
          )}
          {content?.kind === 'pdf' && (
            <iframe
              title={heading}
              src={content.url}
              style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
